/**
 * The trading day, which is a **calendar date and not an instant**, and the two
 * ways that distinction bites.
 *
 * `GET /v1/tickets?businessDate=` takes `YYYY-MM-DD` in Asia/Kolkata (R-006).
 * `ListTicketsQuery` puts two validators on it and explains why neither is
 * sufficient alone: `@IsISO8601()` by itself accepts `2026-08-13T09:14:02Z`,
 * which would reach a `::date` parameter as an instant and quietly answer for a
 * different day; the shape pattern by itself accepts `2026-02-31`, which
 * PostgreSQL refuses when it binds the parameter and which escapes as a
 * *retryable* `SYSTEM_INTERNAL_ERROR`. So a full timestamp is refused and so is
 * 31 February, and this module's job is that neither ever leaves the browser.
 *
 * ## Why a date is not read from a `Date` here either
 *
 * `new Date('2026-08-13')` is midnight **UTC**, which in Asia/Kolkata is 05:30
 * on the 13th. Anything that parses the field into a `Date` and formats it back
 * is a day out for every reader west of the value and, on a machine set to a
 * negative offset, shows the day before the one that was picked. Both functions
 * below therefore work on the digits, and the one `Date` that appears is built
 * from `Date.UTC` and read back in UTC — a round trip that asks the platform
 * what it made of the numbers rather than trusting it with a zone.
 *
 * **No clock is read anywhere in this file**, which is why the screen has no
 * "today" default: knowing what date it is in Asia/Kolkata means asking a
 * machine whose clock this app deliberately does not trust (`lib/clock.ts`,
 * FR-008). The list arrives newest-first by the *server's* clock instead, which
 * is a better answer to "what has happened today" than the device's opinion of
 * what today is.
 */

/**
 * The shape the API's own `ISO_DATE` accepts, restated here because it is the
 * shape of a `<input type="date">` value and this is the guard on the way out.
 *
 * A native date input answers either `''` or a well-formed date, so in the
 * ordinary case this refuses nothing. It exists for the browser that falls back
 * to a text box and for the value pasted into one.
 */
const CALENDAR_DATE_SHAPE = /^(\d{4})-(\d{2})-(\d{2})$/;

export const BUSINESS_DATE_MALFORMED =
  'That is not a date that exists. 31 February is not a date, and the server refuses one rather than reading it as 3 March.';

/**
 * Whether a value names a day that actually happened.
 *
 * The check is a round trip rather than a range test on each part: build the
 * date from the components in UTC, then ask it what it thinks they are. Anything
 * the platform normalised away — 31 February, month 13, day 0 — comes back
 * disagreeing with what went in. Writing the month lengths out by hand would
 * mean owning leap years, which is the same bug one layer down.
 *
 * The same round trip `catalogue/instant.ts` runs for a moment, on a date rather
 * than a date-time. They are deliberately separate: that one reads a
 * `datetime-local` in the reader's own zone and answers a UTC instant, this one
 * reads a calendar date and answers whether it exists. Merging them would mean a
 * function that sometimes applies a zone and sometimes must not.
 */
export function isCalendarDate(value: string): boolean {
  const parts = CALENDAR_DATE_SHAPE.exec(value.trim());

  if (parts === null) return false;

  // `noUncheckedIndexedAccess` is on and a capture group is typed as possibly
  // absent, so each is defaulted. None of them can genuinely be missing — the
  // pattern matched or `exec` answered null.
  const [, year = '', month = '', day = ''] = parts;

  const at = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));

  return (
    at.getUTCFullYear() === Number(year) &&
    at.getUTCMonth() === Number(month) - 1 &&
    at.getUTCDate() === Number(day)
  );
}

/**
 * `2026-08-13` as a person reads it — 13 Aug 2026.
 *
 * Fixed to UTC so the formatter cannot move the day. The value names a calendar
 * date in Asia/Kolkata and carries no time at all, so there is nothing here to
 * convert and the only thing a zone could do is subtract one.
 */
const DAY = new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium', timeZone: 'UTC' });

export function formatBusinessDate(value: string): string {
  const parts = CALENDAR_DATE_SHAPE.exec(value.trim());

  // Shown beside a filter that has already been checked, so this is the
  // unreachable branch rather than the fallback: answering the raw value beats
  // answering `Invalid Date`.
  if (parts === null) return value;

  const [, year = '', month = '', day = ''] = parts;

  return DAY.format(new Date(Date.UTC(Number(year), Number(month) - 1, Number(day))));
}

/**
 * The two functions a calendar widget needs, and the only place a `Date` is
 * allowed to carry a trading day.
 *
 * `react-day-picker` works in `Date` objects and compares them with the **local**
 * getters — `getFullYear`, `getMonth`, `getDate`. That is not a flaw in it; a
 * calendar grid is a local-time idea. But it means the round trip above, which
 * fixes everything to UTC, is the wrong bridge here: `Date.UTC(2026, 7, 13)` is
 * 13 August at 00:00 UTC, and a machine set west of UTC reads that back locally
 * as the **12th** and highlights the wrong square. `toISOString()` on the way out
 * has the same bug pointing the other way — east of UTC it answers the day
 * before.
 *
 * So the pair below is local-midnight in and local parts out, and it must stay a
 * pair: each is only correct because the other matches it. Round-tripped in any
 * zone, `2026-08-13` comes back `2026-08-13`.
 *
 * **This does not weaken the rule at the head of this file.** A trading day
 * still never comes from the device's clock — these convert a value somebody
 * picked, and the string is what leaves the browser. The `Date` exists for the
 * length of the widget's render and no further.
 */
export function toCalendarDate(at: Date): string {
  const year = String(at.getFullYear()).padStart(4, '0');
  const month = String(at.getMonth() + 1).padStart(2, '0');
  const day = String(at.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/** `undefined` for anything a calendar should show nothing selected for. */
export function fromCalendarDate(value: string): Date | undefined {
  const parts = CALENDAR_DATE_SHAPE.exec(value.trim());

  if (parts === null || !isCalendarDate(value)) return undefined;

  const [, year = '', month = '', day = ''] = parts;

  // Local midnight, deliberately — see above.
  return new Date(Number(year), Number(month) - 1, Number(day));
}
